/**
 * useAddToCart Hook
 *
 * TanStack mutation for adding a product to a merchant's cart from Core Frontend
 * (CF-PCARD-1.4). On success it invalidates the multi-cart aggregate so the cart
 * icon/drawer refresh, fires a `cart-item-added` window event for the header
 * animation, and surfaces a notification. The realtime `CART_AGGREGATION_UPDATED`
 * path (useMultiCartSync) still applies — we rely on cache invalidation here and
 * do NOT add a second manual sync.
 *
 * Guests are redirected to /login (mirrors the favorites pattern) without an API call.
 */

import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@teletienda/shared-auth-ui';
import { addCartItem, type AddCartItemResult } from './api';
import { MULTI_CART_QUERY_KEY } from './useMultiCart';
import type { ApiError } from '@/lib/api/client';

/**
 * Variables accepted by the add-to-cart action.
 */
export interface AddToCartVariables {
  /** Merchant that owns the cart (UUID) */
  merchantGuid: string;
  /** Product to add (UUID) */
  productGuid: string;
  /** Defaults to 1 if omitted */
  quantity?: number;
}

/**
 * Hook exposing an `addToCart` action plus mutation state.
 *
 * @example
 * ```tsx
 * const { addToCart, isPending } = useAddToCart();
 * <ProductCard
 *   variant="marketplace"
 *   onAddToCart={(p) => addToCart({ merchantGuid: p.merchantGuid!, productGuid: p.productId })}
 *   addToCartLoading={isPending}
 * />
 * ```
 */
export function useAddToCart() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const mutation = useMutation<AddCartItemResult, ApiError, AddToCartVariables>({
    mutationFn: ({ merchantGuid, productGuid, quantity = 1 }) =>
      addCartItem(merchantGuid, { productGuid, quantity }),
    onSuccess: () => {
      // Refresh the aggregated cart (all currencies) — same key useMultiCartSync uses.
      queryClient.invalidateQueries({ queryKey: MULTI_CART_QUERY_KEY });
      notifications.show({ color: 'green', message: t('cart_actions.added') });
      // Drive the header cart-icon animation (CF-PCARD-1.4 T4).
      window.dispatchEvent(new CustomEvent('cart-item-added'));
    },
    onError: (error) => {
      // ApiError carries a backend code; out-of-stock gets a specific message.
      const code = (error as Partial<ApiError> | undefined)?.code;
      const message =
        code === 'CART_ITEM_UNAVAILABLE'
          ? t('cart_actions.unavailable')
          : t('cart_actions.error');
      notifications.show({ color: 'red', message });
    },
  });

  const addToCart = useCallback(
    (variables: AddToCartVariables) => {
      // Auth gate: guests are sent to login without hitting the API.
      if (!isAuthenticated) {
        notifications.show({ color: 'blue', message: t('cart_actions.login_required') });
        navigate('/login');
        return;
      }
      mutation.mutate(variables);
    },
    [isAuthenticated, navigate, t, mutation]
  );

  return {
    addToCart,
    isPending: mutation.isPending,
    /** Variables of the in-flight add (undefined when idle) — lets callers scope
     * a per-card loading state to the product actually being added (CF-PCARD-1.6). */
    pendingVariables: mutation.isPending ? mutation.variables : undefined,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
